import React, { useState, useEffect } from "react";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import { getAuth } from "firebase/auth";

const RequestPaymentButton = ({ postId }) => {
  const [likeCount, setLikeCount] = useState(null); // State to store likeCount
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRequested, setPaymentRequested] = useState(false); // State to track payment request
  const [userEmail, setUserEmail] = useState(null); // State to store user email

  // Fetch cached post data from `sessionStorage`
  useEffect(() => {
    const fetchPostDataFromCache = () => {
      const cachedPosts = JSON.parse(sessionStorage.getItem("user_posts") || "[]");
      const postData = cachedPosts.find((post) => post.id === postId);

      if (postData) {
        setLikeCount(postData.likeCount || 0); // Set likeCount from cached data
        setPaymentRequested(postData.paymentRequested || false); // Set paymentRequested status
      } else {
        console.error("Post not found in cache");
        setLikeCount(0); // Default to 0 if the post is not in cache
      }
    };

    fetchPostDataFromCache();
  }, [postId]);

  // Fetch cached user email from `sessionStorage`
  useEffect(() => {
    const fetchUserEmailFromCache = () => {
      const cachedUser = JSON.parse(sessionStorage.getItem("user_info"));
      if (cachedUser) {
        setUserEmail(cachedUser.email || "Unknown");
      } else {
        console.error("User data not found in cache");
      }
    };

    fetchUserEmailFromCache();
  }, []);

  const handlePaymentRequest = async () => {
    if (likeCount <= 10) {
      alert("A minimum of 10 likes is required to request payment.");
      return;
    }

    setIsProcessing(true);

    try {
      // Get the current user's UID
      const user = getAuth().currentUser;
      const uid = user ? user.uid : "anonymous";

      // Create a new document in the `paymentRequests` collection
      const paymentRequestRef = doc(db, "paymentRequests", postId);
      await setDoc(paymentRequestRef, {
        uid: uid,
        email: userEmail || "Unknown", // Include the cached email
        postId: postId,
        likeCount: likeCount, // Use the cached `likeCount` value
        timestamp: new Date(),
      });

      // Update the `posts` collection in Firestore to reflect that payment was requested
      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, { paymentRequested: true });

      // Update the local cache for the post
      const cachedPosts = JSON.parse(sessionStorage.getItem("user_posts") || "[]");
      const updatedPosts = cachedPosts.map((post) =>
        post.id === postId ? { ...post, paymentRequested: true } : post
      );
      sessionStorage.setItem("user_posts", JSON.stringify(updatedPosts));

      // Update the state
      setPaymentRequested(true);
      alert("Payment requested successfully!");
    } catch (error) {
      console.error("Error requesting payment:", error.message);
      alert(`Failed to request payment. Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <button
      onClick={handlePaymentRequest}
      disabled={isProcessing || paymentRequested || likeCount <= 10 || likeCount === null} // Disable based on conditions
      className="payment-button"
    >
      {isProcessing
        ? "Processing..."
        : paymentRequested
        ? "Requested"
        : likeCount === null
        ? "Loading..."
        : likeCount <= 10
        ? "Requires 10+ Likes"
        : "Request Payment"}
    </button>
  );
};

export default RequestPaymentButton;
